import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'
import { Slider } from './subcomponents/Slider'
import {
  Col,
  Grid,
  Icon,
  NetworkIcon,
  NotificationsConfirm,
  Switcher,
  Tooltip,
  Typography,
} from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { numberWithSpace } from 'utils/numberWithSpace'
import styles from 'modules/PartnersProgram/subcomponents/RewardCalculator/RewardCalculator.module.scss'
import PageHeader from 'components/PageHeader'

const MIN_VALUE = 30000
const MIN = 0
const MAX = 1000000
const STEP = 10000

const RewardCalculator = ({
  className = '',
  types = [],
  setValues = () => {},
  setReplenishByPartners,
  replenishByPartners,
  loaded,
  awards,
  values,
}) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const TYPES = useMemo(
    () =>
      t('reward_calculator::types', {
        returnObjects: true,
      }),
    [t],
  )

  const handleChange = useCallback((value, network) => {
    setValues((values = []) => {
      const index = values.findIndex((item) => item.network === network)

      if (index > -1) {
        // @ts-ignore
        values.splice(index, 1, { value, network })
        return [...values]
      }
      return [...values, { value, network }]
    })
  }, [])

  const isMinValue = useMemo(
    () => loaded && (awards?.budget_amount ?? 0) < MIN_VALUE,
    [loaded, awards],
  )

  return (
    <div className={clsx(styles['reward-calculator'], className)}>
      <div className={styles['reward-calculator__inner']}>
        <Grid
          gap={24}
          direction={!breakpoint.fb ? 'column' : 'row'}
          wrap={false}
        >
          <Col className={styles['reward-calculator__content-column']}>
            <div className={styles['reward-calculator__content-scroll']}>
              <Typography
                component={'h3'}
                className={
                  !breakpoint.xl ? 'margin-bottom-32' : 'margin-bottom-40'
                }
                variant={
                  !breakpoint.xl
                    ? 'Header-40'
                    : !breakpoint.fb
                    ? 'Header-30'
                    : 'Header-50'
                }
                color={'Label-Primary'}
              >
                {t('reward_calculator::heading')}
              </Typography>
              <div
                className={styles['reward-calculator__content-scroll-inner']}
              >
                <Grid gap={40} direction={'column'} cols={1}>
                  {types?.map(({ network }) => (
                    <Grid
                      direction={!breakpoint.fhd ? 'column' : 'row'}
                      gap={24}
                      key={network}
                      cols={!breakpoint.fhd ? 1 : 2}
                      wrap={false}
                      alignItems={'center'}
                    >
                      <Col>
                        <Grid gap={16} direction={'column'}>
                          <Grid
                            gap={16}
                            alignItems={'center'}
                            justifyContent={'start'}
                            wrap={false}
                          >
                            <Col
                              className={
                                styles['reward-calculator__list-heading-icon']
                              }
                            >
                              <NetworkIcon
                                className={
                                  styles[
                                    'reward-calculator__list-heading-icon-svg'
                                  ]
                                }
                                width={32}
                                rounded
                                name={`${TYPES[network].icon}`}
                              />
                            </Col>

                            <Typography
                              component={'div'}
                              color={'Label-Fifth'}
                              variant={'Body-Accent-30'}
                            >
                              {TYPES[network].label}
                            </Typography>
                          </Grid>
                          <Typography
                            component={'div'}
                            variant={'Body-15'}
                            color={'Label-Fifth'}
                          >
                            {t('reward_calculator::planned_turnover')}
                          </Typography>
                        </Grid>
                      </Col>
                      <Col>
                        <Slider
                          onChange={(value) => handleChange(value, network)}
                          min={MIN}
                          max={MAX}
                          value={
                            values?.find((item) => network === item.network)
                              ?.value ?? 0
                          }
                          step={STEP}
                        />
                      </Col>
                    </Grid>
                  ))}
                </Grid>
              </div>
            </div>
          </Col>

          <Col className={styles['reward-calculator__content-column']}>
            <Grid
              direction={'column'}
              justifyContent={'center'}
              className={clsx(styles['reward-calculator__plans'], 'padding-24')}
            >
              <Grid
                direction={'column'}
                className={styles['partners-program__calc-info']}
              >
                <Grid
                  className={'margin-bottom-24'}
                  direction={'row'}
                  gap={16}
                  wrap={false}
                  alignItems={'center'}
                >
                  <Col>
                    <Switcher
                      checked={replenishByPartners}
                      onChange={() => setReplenishByPartners((state) => !state)}
                    />
                  </Col>
                  <Typography
                    variant={!breakpoint.lg ? 'Header-20' : 'Header-25'}
                  >
                    {t('partners_program::replenishment_via_partners')}
                  </Typography>
                </Grid>
                <Grid
                  className={'margin-bottom-48'}
                  direction={'row'}
                  wrap={false}
                  gap={12}
                >
                  <Icon
                    name={'card'}
                    color={'Interface-Eighth'}
                    style={{ opacity: replenishByPartners ? 0.3 : 1 }}
                  />
                  <Icon
                    name={'document'}
                    color={'Interface-Eighth'}
                    style={{ opacity: replenishByPartners ? 0.3 : 1 }}
                  />
                  <Icon
                    name={'payment-network1'}
                    inheritColor
                    style={{ opacity: replenishByPartners ? 1 : 0.3 }}
                  />
                  <Icon
                    name={'payment-network2'}
                    inheritColor
                    style={{ opacity: replenishByPartners ? 1 : 0.3 }}
                  />
                  <Icon
                    name={'payment-network3'}
                    inheritColor
                    style={{ opacity: replenishByPartners ? 1 : 0.3 }}
                  />
                  <Icon
                    name={'payment-network4'}
                    inheritColor
                    style={{ opacity: replenishByPartners ? 1 : 0.3 }}
                  />
                </Grid>
              </Grid>
              <Grid
                wrap={false}
                className={styles['reward-calculator__plans-turnover']}
                alignItems={'center'}
                gap={10}
              >
                <Typography
                  component={'div'}
                  variant={!breakpoint.lg ? 'Header-20' : 'Header-25'}
                  color={'Label-Secondary'}
                >
                  {t('reward_calculator::planned_turnover_for_month')}
                </Typography>
                <Tooltip
                  className={styles['reward-calculator__info']}
                  title={t('reward_calculator::info_vat')}
                  placement={!breakpoint.fb ? 'top' : 'bottom'}
                >
                  <Icon
                    width={24}
                    name={'information'}
                    color={'Interface-Seventh'}
                  />
                </Tooltip>
              </Grid>
              <PageHeader
                color={'Label-GU-Primary'}
                className={'margin-bottom-24'}
              >
                {t('reward_calculator::value', {
                  value: numberWithSpace(awards?.budget_amount ?? 0),
                })}
              </PageHeader>
              <Grid
                wrap={false}
                alignItems={'center'}
                className={styles['reward-calculator__plans-turnover']}
                gap={10}
              >
                <Typography
                  component={'div'}
                  variant={'Header-20'}
                  color={'Label-Secondary'}
                >
                  {t('reward_calculator::monthly_reward')}
                </Typography>
                <Tooltip
                  title={t('reward_calculator::info_base_awards')}
                  className={styles['reward-calculator__info']}
                  placement={!breakpoint.fb ? 'top' : 'bottom'}
                >
                  <Icon
                    width={24}
                    name={'information'}
                    color={'Interface-Seventh'}
                  />
                </Tooltip>
              </Grid>
              <PageHeader
                color={'Label-GU-Primary'}
                className={clsx(
                  styles['reward-calculator__plans-total'],
                  'margin-bottom-32',
                )}
              >
                {t('reward_calculator::value', {
                  value: isMinValue
                    ? 0
                    : numberWithSpace(awards?.award_amount ?? 0),
                })}
              </PageHeader>
              <Grid
                wrap={false}
                alignItems={'center'}
                className={styles['reward-calculator__plans-turnover']}
                gap={10}
              >
                <Typography
                  component={'div'}
                  variant={'Header-20'}
                  color={'Label-Secondary'}
                >
                  {t('reward_calculator::commission_per_month')}
                </Typography>
                <Tooltip
                  title={t('reward_calculator::info_commission_per_month')}
                  className={styles['reward-calculator__info']}
                  placement={!breakpoint.fb ? 'top' : 'bottom'}
                >
                  <Icon
                    width={24}
                    name={'information'}
                    color={'Interface-Seventh'}
                  />
                </Tooltip>
              </Grid>
              <PageHeader
                color={'Label-GU-Primary'}
                className={clsx(
                  styles['reward-calculator__plans-total'],
                  'margin-bottom-32',
                )}
              >
                {t('reward_calculator::value', {
                  value: isMinValue
                    ? 0
                    : numberWithSpace(awards?.commission_amount ?? 0),
                })}
              </PageHeader>
              {isMinValue && (
                <NotificationsConfirm
                  className={'margin-bottom-24'}
                  variant={'error'}
                  CloseButton={false}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('reward_calculator::min_value_error', {
                        value: numberWithSpace(MIN_VALUE, '&nbsp;'),
                      }),
                    }}
                  />
                </NotificationsConfirm>
              )}
              <Typography
                variant={'Body-15'}
                color={'Label-Fifth'}
                className={styles['reward-calculator__plans-summary']}
              >
                {t('reward_calculator::summary')}
              </Typography>
            </Grid>
          </Col>
        </Grid>
      </div>
    </div>
  )
}

RewardCalculator.propTypes = {
  className: PropTypes.string,
  types: PropTypes.array,
  getTypes: PropTypes.func,
  setValues: PropTypes.func,
  awards: PropTypes.shape({
    budget_amount: PropTypes.number,
    award_amount: PropTypes.number,
    commission_amount: PropTypes.number,
  }),
  values: PropTypes.array,
  setReplenishByPartners: PropTypes.func,
  replenishByPartners: PropTypes.bool,
  loaded: PropTypes.bool,
}

export default RewardCalculator
